import { apiClient } from '@/lib/api-client';
import {
  Task,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskFilters,
} from '@/types';

export interface BulkUpdateTasksRequest {
  taskIds: string[];
  updateData: UpdateTaskRequest;
}

export interface BulkUpdateTasksResponse {
  updatedTasks: Task[];
  successCount: number;
  totalRequested: number;
}

export class TasksService {
  /**
   * Get all tasks (with optional filters)
   */
  async getTasks(filters?: TaskFilters): Promise<Task[]> {
    const queryParams = new URLSearchParams();

    if (filters) {
      if (filters.clientId) queryParams.append('clientId', filters.clientId);
      if (filters.projectId) queryParams.append('projectId', filters.projectId);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
    }

    const query = queryParams.toString();
    return await apiClient.get<Task[]>(`/tasks${query ? `?${query}` : ''}`);
  }

  /**
   * Get a specific task by ID
   */
  async getTask(id: string): Promise<Task> {
    return await apiClient.get<Task>(`/tasks/${id}`);
  }

  /**
   * Create a new task
   */
  async createTask(data: CreateTaskRequest): Promise<Task> {
    return await apiClient.post<Task>('/tasks', data);
  }

  /**
   * Update an existing task
   */
  async updateTask(id: string, data: UpdateTaskRequest): Promise<Task> {
    return await apiClient.put<Task>(`/tasks/${id}`, data);
  }

  /**
   * Delete a task (soft delete)
   */
  async deleteTask(id: string): Promise<void> {
    return await apiClient.delete<void>(`/tasks/${id}`);
  }

  /**
   * Bulk update multiple tasks
   */
  async bulkUpdateTasks(data: BulkUpdateTasksRequest): Promise<BulkUpdateTasksResponse> {
    return await apiClient.post<BulkUpdateTasksResponse>('/tasks/bulk-update', data);
  }

  /**
   * Add a comment to a task
   */
  async addComment(taskId: string, text: string): Promise<Task> {
    return await apiClient.post<Task>(`/tasks/${taskId}/comments`, { text });
  }

  /**
   * Delete a comment from a task
   */
  async deleteComment(taskId: string, commentId: string): Promise<Task> {
    return await apiClient.delete<Task>(`/tasks/${taskId}/comments/${commentId}`);
  }
}

export const tasksService = new TasksService();
